import { useQuery } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { api } from "@/shared/api";
import {
  chooseOrganisation,
  forgetOrganisation,
  signInTenantId,
  type ChosenOrganisation,
} from "../api/tenant";

/** `GET /auth/organisations/{code}` — a code, or an organisation's id (backend D202). */
export async function findOrganisation(code: string): Promise<ChosenOrganisation> {
  const envelope = await api.findOrganisation({ path: { code: code.trim() } });
  return { tenantId: envelope.data.tenant_id, name: envelope.data.display_name };
}

/**
 * Which organisation a signed-out screen is acting for, and how to change it.
 *
 * `tenantId` is the chosen organisation's, else the build's configured one, else empty (the
 * screen then asks for a code). `name` is looked up so the person sees where they are signing
 * in before typing a password; while it loads, or if it cannot be found, it is `null`.
 */
export function useSignInOrganisation(linkCode: string | null = null): {
  tenantId: string;
  name: string | null;
  /** True while a sign-in link's organisation is being looked up. */
  resolving: boolean;
  choose: (code: string) => Promise<ChosenOrganisation>;
  clear: () => void;
} {
  // A link naming an organisation (`?org=`) waits for it, rather than showing the build's
  // default for the second the lookup takes — a fast sign-in in that window went to the wrong
  // organisation (pilot onboarding finding).
  const hasLink = linkCode !== null && linkCode.trim() !== "";
  const [tenantId, setTenantId] = useState(() => (hasLink ? "" : signInTenantId()));
  const [resolving, setResolving] = useState(hasLink);
  const named = useQuery({
    queryKey: ["sign-in-organisation", tenantId],
    queryFn: () => findOrganisation(tenantId),
    enabled: tenantId !== "",
    retry: false,
    staleTime: Infinity,
  });
  const choose = useCallback(async (code: string) => {
    try {
      const organisation = await findOrganisation(code);
      chooseOrganisation(organisation);
      setTenantId(organisation.tenantId);
      return organisation;
    } finally {
      setResolving(false);
    }
  }, []);
  const clear = useCallback(() => {
    forgetOrganisation();
    // A build with a configured organisation falls back to it; "different organisation"
    // then means typing a code, so the field is shown by emptying the choice.
    setTenantId("");
  }, []);
  return {
    tenantId,
    name: tenantId === "" ? null : (named.data?.name ?? null),
    resolving,
    choose,
    clear,
  };
}

